import { Button, Card, Flex, Tag, Typography } from 'antd'
import { Crown, TriangleAlert } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ConfirmActionModal } from 'src/components'
import { useSessionContext } from 'src/hooks'
import { isWorkspaceOwnerFromSession } from 'src/utils/workspaceMenuAccess'

import { SettingRow } from './SettingRow'
import { TransferOwnershipModal } from './TransferOwnershipModal'

const { Text } = Typography

type Props = {
  workspaceId: number
  workspaceName: string
  onDelete: () => void
  deleteLoading?: boolean
}

export const DangerZoneSection = ({
  workspaceId,
  workspaceName,
  onDelete,
  deleteLoading
}: Props) => {
  const { t } = useTranslation()
  const { user } = useSessionContext()
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [transferOpen, setTransferOpen] = useState(false)

  // The whole danger zone is owner-only. Transfer is an owner-only endpoint (an
  // admin only gets a 403), and deleting the workspace is restricted to the
  // owner too — so an admin sees no danger zone rather than an empty card or a
  // button that would 403.
  const isOwner = isWorkspaceOwnerFromSession(user, workspaceId)

  if (!isOwner) {
    return null
  }

  return (
    <>
      <Card
        variant="outlined"
        style={{ borderColor: 'var(--ant-color-error-border)' }}
        styles={{
          header: {
            background: 'var(--ant-color-error-bg)',
            borderColor: 'var(--ant-color-error-border)'
          }
        }}
        title={
          <Flex align="center" gap={6}>
            <TriangleAlert size={16} color="var(--ant-color-error)" />
            <Text
              strong
              style={{ color: 'var(--ant-color-error)', fontSize: 14 }}
            >
              {t('workspace.settings.dangerZone')}
            </Text>
          </Flex>
        }
      >
        <SettingRow
          danger
          title={t('workspace.settings.danger.transferTitle')}
          description={t('workspace.settings.danger.transferDescription')}
          control={
            <Button
              size="small"
              danger
              icon={<Crown size={14} />}
              onClick={() => setTransferOpen(true)}
            >
              {t('workspace.settings.danger.transferButton')}
            </Button>
          }
        />

        <SettingRow
          danger
          last
          title={t('workspace.settings.danger.deleteTitle')}
          description={
            <span>
              {t('workspace.settings.danger.deleteDescription')}{' '}
              <Text strong style={{ color: 'var(--ant-color-error)' }}>
                {t('workspace.settings.danger.deleteWarning')}
              </Text>
            </span>
          }
          control={
            <Button
              size="small"
              danger
              type="primary"
              loading={deleteLoading}
              onClick={() => setDeleteOpen(true)}
            >
              {t('workspace.settings.danger.deleteButton')}
            </Button>
          }
        />

        {/* Workspace metadata tag — context for the danger zone */}
        <Flex
          align="center"
          gap={6}
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: '1px solid var(--ant-color-error-border)'
          }}
        >
          <Text type="secondary" style={{ fontSize: 11 }}>
            {t('workspace.settings.danger.acting')}
          </Text>
          <Tag style={{ marginInlineEnd: 0 }}>{workspaceName}</Tag>
        </Flex>
      </Card>

      <ConfirmActionModal
        open={deleteOpen}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={() => {
          setDeleteOpen(false)
          onDelete()
        }}
        title={t('workspace.settings.danger.deleteConfirmTitle')}
        description={t('workspace.settings.danger.deleteConfirmDescription', {
          name: workspaceName
        })}
        confirmText={workspaceName}
        confirmPlaceholder={workspaceName}
        confirmButtonText={t('workspace.settings.danger.deleteButton')}
        loading={deleteLoading}
        warningMessage={t('workspace.settings.danger.deleteWarningBlock')}
      />

      <TransferOwnershipModal
        open={transferOpen}
        workspaceId={workspaceId}
        onClose={() => setTransferOpen(false)}
      />
    </>
  )
}
